import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import MatchDetailsTab from './MatchDetailsTab';
import LineupTab from './LineupTab';
import ScoresheetTab from './ScoresheetTab';
import KeyEventsTab from './KeyEventsTab';
import ConfigTab from './ConfigTab';
import ScoreAppHeader from '../../../shared/components/layout/ScoreAppHeader';
import { useMatchDetails } from '../../../shared/hooks/useMatchDetails';
import { useMatchTimeline } from '../../../shared/hooks/useMatchTimeline';
import { useRealtimeScores } from '../../../shared/hooks/useRealtimeScores';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import Toast from '../../../shared/components/ui/Toast';
import UnsavedChangesModal from '../../../shared/components/ui/UnsavedChangesModal';
// Push Events functionality moved to Config tab
import { matchUpdateService, MatchUpdateData } from '../../../shared/services/matchUpdate';
import VideoLinkModal from '../../../shared/components/ui/VideoLinkModal';
import BreakEventTimingModal from '../../../shared/components/ui/BreakEventTimingModal';
import { matchVideosService } from '../../../shared/services/matchVideosService';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { VideoPlayerMethods } from '../../../shared/components/video/types';

const MatchDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'details' | 'lineups' | 'scoresheet' | 'keyevents' | 'config'>('details');
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [toastTimeout, setToastTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  // Push Events functionality moved to Config tab
  const [isVideoLinkModalOpen, setIsVideoLinkModalOpen] = useState(false);
  const videoPlayerRef = useRef<VideoPlayerMethods | null>(null);
  const videoTimeRef = useRef<number | null>(null);
  const videoFallbackIdRef = useRef<string | null>(null);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const handleVideoContextChange = (context: { currentVideoId: string | null; currentVideoTime?: number | null }) => {
    if (context.currentVideoId !== undefined && context.currentVideoId !== selectedVideoId) {
      setSelectedVideoId(context.currentVideoId);
    }
    if (context.currentVideoTime !== undefined) {
      videoTimeRef.current = context.currentVideoTime;
    }
  };

  const getVideoTimingForGameFlow = async (): Promise<{ videoTimeSeconds: number | null; videoId: string | null }> => {
    let videoTimeSeconds: number | null = null;
    let videoId: string | null = selectedVideoId ?? videoFallbackIdRef.current ?? null;

    if (videoPlayerRef.current) {
      try {
        const currentTime = videoPlayerRef.current.getCurrentTime();
        if (typeof currentTime === 'number' && !isNaN(currentTime)) {
          videoTimeSeconds = currentTime;
        }
      } catch (error) {
        console.warn('Unable to read video time for timeline events:', error);
      }
    }

    if (videoTimeSeconds === null && videoTimeRef.current !== null) {
      videoTimeSeconds = videoTimeRef.current;
    }

    if (!videoId && actualMatchId) {
      try {
        const videosResult = await matchVideosService.getMatchVideos(actualMatchId);
        if (videosResult.success && videosResult.data && videosResult.data.length > 0) {
          videoId = videosResult.data[0].id;
          videoFallbackIdRef.current = videoId;
        }
      } catch (error) {
        console.warn('Could not fetch fallback video for timeline events:', error);
      }
    }

    return { videoTimeSeconds, videoId };
  };

  
  // State for publish all confirmation
  const [showPublishAllDialog, setShowPublishAllDialog] = useState(false);
  const [isPublishingAll, setIsPublishingAll] = useState(false);

  // State for break event timing modal
  const [breakEventModal, setBreakEventModal] = useState<{
    isOpen: boolean;
    eventType: 'start_2nd_half' | 'start_extra_time_1st_half' | 'start_extra_time_2nd_half' | null;
    eventLabel: string;
    defaultMinute: number;
    handler: (videoTimeSeconds: number | null, matchMinute: number, videoId: string | null) => Promise<void>;
  }>({
    isOpen: false,
    eventType: null,
    eventLabel: '',
    defaultMinute: 45,
    handler: async () => {}
  });
  
  // State to track form changes
  const [matchUpdates, setMatchUpdates] = useState<MatchUpdateData>({});
  const [officialsUpdates, setOfficialsUpdates] = useState<Record<string, string>>({});

  // Function to show toast with auto-close
  const showToastWithAutoClose = (message: string, type: 'success' | 'error' | 'info') => {
    // Clear existing timeout
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }
    
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    // Set auto-close timeout
    const timeout = setTimeout(() => {
      setShowToast(false);
    }, 5000);
    
    setToastTimeout(timeout);
  };

  // Extract the actual match ID from the URL path since useParams is not working
  const extractMatchIdFromPath = () => {
    const path = location.pathname;
    // Extract full UUID from patterns like /score/{matchId}-match-details or /o/{orgId}/score/{matchId}-match-details
    // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const match = path.match(/\/score\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})-/);
    return match ? match[1] : '';
  };

  const actualMatchId = extractMatchIdFromPath();

  // Debug: Log the extracted match ID (temporarily for troubleshooting)
  console.log('Path:', location.pathname);
  console.log('Extracted actualMatchId from path:', actualMatchId);

  // Fetch match details from database
  const { matchDetails, loading, error, refetch } = useMatchDetails(actualMatchId);
  
  // Get penalty shootout scores from match details
  const penaltyData = matchDetails?.home_penalty_score !== undefined && matchDetails?.away_penalty_score !== undefined && 
    (matchDetails.home_penalty_score > 0 || matchDetails.away_penalty_score > 0) ? {
      homeScored: matchDetails.home_penalty_score,
      awayScored: matchDetails.away_penalty_score
    } : null;
  
  // Match timeline integration
  const { 
    matchStatus, 
    isLoading: timelineLoading, 
    startMatch,
    endFirstHalf,
    setFullTime,
    resetMatch,
    refreshStatus,
    // Extra-time controls
    endExtraTimeFirstHalf,
    startPenaltyShootout,
    // Editing controls
    enableEditingMode,
    disableEditingMode
  } = useMatchTimeline(actualMatchId);
  
  // Real-time scores hook (no page refresh)
  const { scores: realtimeScores } = useRealtimeScores(actualMatchId);
  
  // State for confirmation dialogs
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
  const [confirmMessage, setConfirmMessage] = useState('');
  
  
  // Confirmation dialog handlers
  const showConfirmation = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmDialog(true);
  };
  
  const handleConfirm = () => {
    confirmAction();
    setShowConfirmDialog(false);
  };
  
  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  // Handle publish all
  const handlePublishAllClick = () => {
    setShowPublishAllDialog(true);
  };

  const handleConfirmPublishAll = async () => {
    setIsPublishingAll(true);
    try {
      if ((window as any).keyEventsPublishAllHandler) {
        await (window as any).keyEventsPublishAllHandler();
        showToastWithAutoClose('All key events published successfully!', 'success');
      }
      setShowPublishAllDialog(false);
    } catch (error) {
      console.error('Error publishing all events:', error);
      showToastWithAutoClose('Failed to publish all events', 'error');
    } finally {
      setIsPublishingAll(false);
    }
  };
  
  // Match control handlers
  const handleStartMatch = () => {
    showConfirmation(
      'Are you sure you want to start the match?',
      async () => {
        try {
          const { videoTimeSeconds, videoId } = await getVideoTimingForGameFlow();
          await startMatch({
            videoTimeSeconds: videoTimeSeconds ?? undefined,
            videoId: videoId ?? undefined
          });
          showToastWithAutoClose('Match started successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to start match', 'error');
        }
      }
    );
  };
  
  const handleEndFirstHalf = () => {
    showConfirmation(
      'Are you sure you want to end the first half?',
      async () => {
        try {
          const { videoTimeSeconds, videoId } = await getVideoTimingForGameFlow();
          await endFirstHalf({
            minuteOverride: matchStatus.current_minute,
            videoTimeSeconds: videoTimeSeconds ?? undefined,
            videoId: videoId ?? undefined
          });
          showToastWithAutoClose('First half ended successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to end first half', 'error');
        }
      }
    );
  };

  const handleStartSecondHalf = async () => {
    setBreakEventModal({
      isOpen: true,
      eventType: 'start_2nd_half',
      eventLabel: 'Start Second Half',
      defaultMinute: 45,
      handler: async (videoTimeSeconds, matchMinute, videoId) => {
        try {
          const { MatchTimelineService } = await import('../../../shared/services/matchTimelineService');
          await MatchTimelineService.recordMatchEvent(
            actualMatchId,
            'start_2nd_half',
            matchMinute,
            undefined,
            undefined,
            videoTimeSeconds ?? undefined,
            videoId ?? undefined
          );
          showToastWithAutoClose('Second half started successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to start second half', 'error');
        }
      }
    });
  };
  
  const handleFullTime = () => {
    showConfirmation(
      'Are you sure you want to record full time?',
      async () => {
        try {
          const { videoTimeSeconds, videoId } = await getVideoTimingForGameFlow();
          await setFullTime({
            minuteOverride: matchStatus.current_minute,
            videoTimeSeconds: videoTimeSeconds ?? undefined,
            videoId: videoId ?? undefined
          });
          showToastWithAutoClose('Full time recorded successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to record full time', 'error');
        }
      }
    );
  };
  
  const handleResetMatch = () => {
    showConfirmation(
      'This will reset all match data. Are you sure you want to continue?',
      async () => {
        try {
          await resetMatch();
          showToastWithAutoClose('Match reset successfully!', 'success');
          // Refresh match status and details after reset (reset changes match data)
          await Promise.all([refetch(), refreshStatus()]);
        } catch (error) {
          showToastWithAutoClose('Failed to reset match', 'error');
        }
      }
    );
  };

  // Extra-time control handlers
  const handleStartExtraTimeFirstHalf = async () => {
    setBreakEventModal({
      isOpen: true,
      eventType: 'start_extra_time_1st_half',
      eventLabel: 'Start Extra Time First Half',
      defaultMinute: 90,
      handler: async (videoTimeSeconds, matchMinute, videoId) => {
        try {
          const { MatchTimelineService } = await import('../../../shared/services/matchTimelineService');
          await MatchTimelineService.recordMatchEvent(
            actualMatchId,
            'start_extra_time_1st_half',
            matchMinute,
            undefined,
            undefined,
            videoTimeSeconds ?? undefined,
            videoId ?? undefined
          );
          showToastWithAutoClose('Extra time first half started successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to start extra time first half', 'error');
        }
      }
    });
  };

  const handleEndExtraTimeFirstHalf = () => {
    showConfirmation(
      'Are you sure you want to end extra time first half?',
      async () => {
        try {
          await endExtraTimeFirstHalf();
          showToastWithAutoClose('Extra time first half ended successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to end extra time first half', 'error');
        }
      }
    );
  };

  const handleStartExtraTimeSecondHalf = async () => {
    setBreakEventModal({
      isOpen: true,
      eventType: 'start_extra_time_2nd_half',
      eventLabel: 'Start Extra Time Second Half',
      defaultMinute: 105,
      handler: async (videoTimeSeconds, matchMinute, videoId) => {
        try {
          const { MatchTimelineService } = await import('../../../shared/services/matchTimelineService');
          await MatchTimelineService.recordMatchEvent(
            actualMatchId,
            'start_extra_time_2nd_half',
            matchMinute,
            undefined,
            undefined,
            videoTimeSeconds ?? undefined,
            videoId ?? undefined
          );
          showToastWithAutoClose('Extra time second half started successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to start extra time second half', 'error');
        }
      }
    });
  };

  const handleStartPenaltyShootout = () => {
    showConfirmation(
      'Are you sure you want to start penalty shootout? This will begin the penalty shootout phase.',
      async () => {
        try {
          await startPenaltyShootout(120); // Default to minute 120
          showToastWithAutoClose('Penalty shootout started successfully!', 'success');
          // Refresh match status (match details don't change with game flow events)
          await refreshStatus();
        } catch (error) {
          showToastWithAutoClose('Failed to start penalty shootout', 'error');
        }
      }
    );
  };

  // Determine active tab from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('-lineup')) {
      setActiveTab('lineups');
    } else if (path.includes('-scoresheet')) {
      setActiveTab('scoresheet');
    } else if (path.includes('-keyevents')) {
      setActiveTab('keyevents');
    } else if (path.includes('-config')) {
      setActiveTab('config');
    } else {
      setActiveTab('details');
    }
  }, [location.pathname]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeout) {
        clearTimeout(toastTimeout);
      }
    };
  }, [toastTimeout]);

  // Debug: Log match status for troubleshooting
  console.log('Match Status Debug:', {
    matchStatus,
    timelineLoading,
    actualMatchId
  });

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state
  if (error || !matchDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Match Details</h2>
          <p className="text-gray-600 mb-4">{error || 'Match not found'}</p>
          <Button onClick={() => navigate('/score')} variant="primary">
            Back to Matches
          </Button>
        </div>
      </div>
    );
  }

  // Convert database data to UI format
  const formatMatchDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatMatchTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle match details updates
  const handleMatchUpdate = (updates: Partial<MatchUpdateData>) => {
    setMatchUpdates((prev: MatchUpdateData) => ({ ...prev, ...updates }));
  };

  // Handle officials updates
  const handleOfficialsUpdate = (role: string, officialId: string) => {
    setOfficialsUpdates(prev => ({ ...prev, [role]: officialId }));
  };

  // Push Events functionality has been moved to Config tab
  // Use Config tab to push events (Timeline, Key Events, Visualisation, Video Tags)

  const handleSave = async () => {
    if (!actualMatchId) {
      showToastWithAutoClose('No match ID found', 'error');
      return;
    }

    setIsSaving(true);

    try {
      // If we're on the lineup tab, handle lineup save differently
      if (activeTab === 'lineups') {
        // Check if there's a lineup save handler available
        if ((window as any).lineupSaveHandler) {
          await (window as any).lineupSaveHandler();
          setIsSaving(false);
          return;
        } else {
          showToastWithAutoClose('Lineup save handler not available', 'error');
          setIsSaving(false);
          return;
        }
      }

      // If we're on the scoresheet tab, handle scoresheet save differently
      if (activeTab === 'scoresheet') {
        // Check if there's a scoresheet save handler available
        if ((window as any).scoresheetSaveHandler) {
          await (window as any).scoresheetSaveHandler();
          setIsSaving(false);
          return;
        } else {
          showToastWithAutoClose('Scoresheet save handler not available', 'error');
          setIsSaving(false);
          return;
        }
      }

      // Check if there are any updates to save
      const hasMatchUpdates = Object.keys(matchUpdates).length > 0;
      const hasOfficialsUpdates = Object.keys(officialsUpdates).length > 0;

      if (!hasMatchUpdates && !hasOfficialsUpdates) {
        showToastWithAutoClose('No changes to save', 'info');
        setIsSaving(false);
        return;
      }

      let savedSections: string[] = [];

      // Save match details if there are updates
      if (hasMatchUpdates) {
        await matchUpdateService.updateMatchDetails(actualMatchId, matchUpdates);
        savedSections.push('Match Information');
        setMatchUpdates({}); // Clear updates
      }

      // Save officials if there are updates
      if (hasOfficialsUpdates) {
        await matchUpdateService.updateMatchOfficials(actualMatchId, officialsUpdates);
        savedSections.push('Match Officials');
        setOfficialsUpdates({}); // Clear updates
      }

      // Refetch data to ensure UI shows latest saved data
      await refetch();

      // Show success message
      const message = savedSections.length === 1 
        ? `${savedSections[0]} updated successfully!`
        : `${savedSections.join(' and ')} updated successfully!`;
      
      showToastWithAutoClose(message, 'success');

    } catch (error) {
      console.error('Error saving match details:', error);
      showToastWithAutoClose('Failed to save changes. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const hasMatchUpdates = Object.keys(matchUpdates).length > 0;
    const hasOfficialsUpdates = Object.keys(officialsUpdates).length > 0;
    return hasMatchUpdates || hasOfficialsUpdates;
  };

  const handleBack = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      navigate('/score');
    }
  };

  const handleGoBackWithoutSaving = () => {
    setShowUnsavedModal(false);
    navigate('/score');
  };

  const handleSaveAndGoBack = async () => {
    setShowUnsavedModal(false);
    await handleSave();
    // Navigate after successful save
    setTimeout(() => {
      navigate('/score');
    }, 1000);
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: '📋' },
    { id: 'lineups', label: 'Lineups', icon: '👥' },
    { id: 'scoresheet', label: 'Scoresheet', icon: '📊' },
    { id: 'keyevents', label: 'Events', icon: '⚡' },
    { id: 'config', label: 'Config', icon: '⚙️' }
  ] as const;

  // Handle tab navigation with organization context
  const handleTabChange = (tabId: 'details' | 'lineups' | 'scoresheet' | 'keyevents' | 'config') => {
    const baseMatchId = actualMatchId;
    
    if (!baseMatchId) {
      // console.error('No match ID available for navigation');
      return;
    }
    
    // Extract organization ID from current URL to maintain organization context
    const orgIdFromPath = extractOrgIdFromPath(location.pathname);
    const basePath = orgIdFromPath ? `/o/${orgIdFromPath}/score` : '/score';
    
    switch (tabId) {
      case 'details':
        navigate(`${basePath}/${baseMatchId}-match-details`);
        break;
      case 'lineups':
        navigate(`${basePath}/${baseMatchId}-lineup`);
        break;
      case 'scoresheet':
        navigate(`${basePath}/${baseMatchId}-scoresheet`);
        break;
      case 'keyevents':
        navigate(`${basePath}/${baseMatchId}-keyevents`);
        break;
      case 'config':
        navigate(`${basePath}/${baseMatchId}-config`);
        break;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <MatchDetailsTab 
            matchDetails={matchDetails} 
            onUpdate={handleMatchUpdate}
            onOfficialsUpdate={handleOfficialsUpdate}
          />
        );
      case 'lineups':
        return <LineupTab />;
      case 'scoresheet':
        return <ScoresheetTab 
          matchDetails={{
            id: actualMatchId,
            homeTeam: {
              id: matchDetails?.home_team?.id || '',
              name: matchDetails?.home_team?.name || 'Home Team',
              logo: matchDetails?.home_team?.logo || '🏠',
              score: realtimeScores?.home_score ?? matchDetails?.home_score ?? 0
            },
            awayTeam: {
              id: matchDetails?.away_team?.id || '',
              name: matchDetails?.away_team?.name || 'Away Team',
              logo: matchDetails?.away_team?.logo || '🏠',
              score: realtimeScores?.away_score ?? matchDetails?.away_score ?? 0
            }
          }}
          matchStatus={matchStatus}
          enableEditingMode={enableEditingMode}
          disableEditingMode={disableEditingMode}
          videoPlayerRef={videoPlayerRef}
          onVideoContextChange={handleVideoContextChange}
        />;
      case 'keyevents':
        return <KeyEventsTab 
          matchDetails={{
            id: actualMatchId,
            homeTeam: {
              id: matchDetails?.home_team?.id || '',
              name: matchDetails?.home_team?.name || 'Home Team',
              logo: matchDetails?.home_team?.logo || '🏠',
              score: realtimeScores?.home_score ?? matchDetails?.home_score ?? 0
            },
            awayTeam: {
              id: matchDetails?.away_team?.id || '',
              name: matchDetails?.away_team?.name || 'Away Team',
              logo: matchDetails?.away_team?.logo || '🏠',
              score: realtimeScores?.away_score ?? matchDetails?.away_score ?? 0
            }
          }}
          {...(activeTab === 'keyevents' && { isActive: true })}
        />;
      case 'config':
        return (
          <ConfigTab 
            matchId={actualMatchId} 
            homeTeamId={matchDetails?.home_team_id}
            awayTeamId={matchDetails?.away_team_id}
            onResetMatch={handleResetMatch}
          />
        );
      default:
        return (
          <MatchDetailsTab 
            matchDetails={matchDetails} 
            onUpdate={handleMatchUpdate}
            onOfficialsUpdate={handleOfficialsUpdate}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Consistent Header */}
      <ScoreAppHeader
        leftContent={
          <Button
            onClick={handleBack}
            variant="ghost"
            size="md"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            }
            iconPosition="left"
          >
            Back to Matches
          </Button>
        }
        centerContent={
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-gray-400 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        }
        rightContent={
          activeTab === 'keyevents' ? (
            <div className="flex items-center space-x-2">
              {/* +Event Button */}
              <Button
                onClick={() => {
                  if ((window as any).keyEventsCreateHandler) {
                    (window as any).keyEventsCreateHandler();
                  }
                }}
                variant="secondary"
                size="md"
                className="min-w-[80px] transition-all duration-200"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                }
              >
                Event
              </Button>
              
              {/* Publish All button */}
              <Button
                onClick={handlePublishAllClick}
                variant="primary"
                size="md"
                disabled={isPublishingAll}
                className="min-w-[100px] transition-all duration-200"
              >
                {isPublishingAll ? 'Publishing...' : 'Publish All'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              {/* Link Video Button */}
              <Button
                onClick={() => setIsVideoLinkModalOpen(true)}
                variant="secondary"
                size="md"
                className="min-w-[80px] transition-all duration-200"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                }
              >
                Video
              </Button>
              
              {/* Save button with icon */}
              <Button
                onClick={handleSave}
                variant="primary"
                size="md"
                disabled={isSaving}
                className="min-w-[80px] transition-all duration-200"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                }
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )
        }
      />
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* Match Header */}
        <div className="bg-gradient-to-r from-gray-100 via-gray-200 to-gray-300 rounded-xl p-4 mb-6 text-gray-800 relative overflow-hidden">
          {/* Match Controls */}
          <div className="absolute top-2 left-4 flex space-x-2">
            {matchStatus.status === 'not_started' && (
              <Button
                onClick={handleStartMatch}
                variant="primary"
                size="sm"
                disabled={timelineLoading}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold"
              >
                Start Match
              </Button>
            )}
            
            {/* First Half Controls */}
            {matchStatus.status === 'live' && !matchStatus.start_2nd_half_minute && (
              <>
                <Button
                  onClick={handleEndFirstHalf}
                  variant="secondary"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
                >
                  End First Half
                </Button>
                <Button
                  onClick={handleFullTime}
                  variant="danger"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Full Time
                </Button>
              </>
            )}
            
            {/* Half Time Controls */}
            {matchStatus.status === 'half_time' && (
              <>
                <Button
                  onClick={handleStartSecondHalf}
                  variant="primary"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  Start 2nd Half
                </Button>
                <Button
                  onClick={handleFullTime}
                  variant="danger"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Full Time
                </Button>
              </>
            )}
            
            {/* Second Half Controls */}
            {matchStatus.status === 'live' && matchStatus.start_2nd_half_minute && !matchStatus.start_extra_time_1st_half_minute && (
              <>
                <Button
                  onClick={handleStartExtraTimeFirstHalf}
                  variant="secondary"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                >
                  Start Extra Time
                </Button>
                <Button
                  onClick={handleStartPenaltyShootout}
                  variant="secondary"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                >
                  Penalty Shootout
                </Button>
                <Button
                  onClick={handleFullTime}
                  variant="danger"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Full Time
                </Button>
              </>
            )}
            
            {/* Extra Time First Half Controls */}
            {matchStatus.status === 'extra_time_1st_half' && (
              <>
                <Button
                  onClick={handleEndExtraTimeFirstHalf}
                  variant="secondary"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
                >
                  End Extra Time 1st Half
                </Button>
                <Button
                  onClick={handleFullTime}
                  variant="danger"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Full Time
                </Button>
              </>
            )}
            
            {/* Extra Time Half Time Controls */}
            {matchStatus.status === 'extra_time_half_time' && (
              <>
                <Button
                  onClick={handleStartExtraTimeSecondHalf}
                  variant="primary"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  Start Extra Time 2nd Half
                </Button>
                <Button
                  onClick={handleFullTime}
                  variant="danger"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Full Time
                </Button>
              </>
            )}
            
            {/* Extra Time Second Half Controls */}
            {matchStatus.status === 'extra_time_2nd_half' && (
              <>
                <Button
                  onClick={handleStartPenaltyShootout}
                  variant="secondary"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                >
                  Penalty Shootout
                </Button>
                <Button
                  onClick={handleFullTime}
                  variant="danger"
                  size="sm"
                  disabled={timelineLoading}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Full Time
                </Button>
              </>
            )}
            
            {/* Penalty Shootout Controls */}
            {matchStatus.status === 'penalty_shootout' && (
              <Button
                onClick={handleFullTime}
                variant="danger"
                size="sm"
                disabled={timelineLoading}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                Full Time
              </Button>
            )}
          </div>


          {/* Match Status Badge - Top Right */}
          <div className={`absolute top-2 right-4 px-3 py-1 rounded-full text-xs font-semibold ${
            matchStatus.status === 'not_started' ? 'bg-gray-100 text-gray-700' :
            matchStatus.status === 'live' ? 'bg-green-100 text-green-700' :
            matchStatus.status === 'half_time' ? 'bg-yellow-100 text-yellow-700' :
            matchStatus.status === 'editing' ? 'bg-blue-100 text-blue-700' :
            'bg-red-100 text-red-700'
          }`}>
            {matchStatus.status === 'not_started' ? 'Yet to Start' :
             matchStatus.status === 'live' ? `LIVE` :
             matchStatus.status === 'half_time' ? `Half Time` :
             matchStatus.status === 'editing' ? `Editing` :
             `Full Time`}
          </div>
          
          <div className="text-center space-y-3">
            {/* Row 1: Date, Time, Tournament */}
            <div className="flex justify-center items-center space-x-8 text-xs">
              <span className="font-medium">{formatMatchDate(matchDetails.start_date)}</span>
              <span className="font-medium">{formatMatchTime(matchDetails.start_date)}</span>
              <div className="flex items-center space-x-2">
                <img 
                  src={matchDetails.tournament?.logo || 'https://via.placeholder.com/16x16/cccccc/666666?text=🏆'} 
                  alt={`${matchDetails.tournament?.name || 'League'} logo`}
                  className="w-4 h-4 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/16x16/cccccc/666666?text=🏆';
                  }}
                />
                <span className="font-medium">{matchDetails.tournament?.name || 'League'}</span>
              </div>
            </div>
            
            {/* Row 2: Team A, Score, Team B */}
            <div className="flex justify-center items-center space-x-8">
              <div className="flex items-center space-x-3">
                <img 
                  src={matchDetails.home_team?.logo || 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️'} 
                  alt={`${matchDetails.home_team?.name || 'Home Team'} logo`}
                  className="w-8 h-8 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️';
                  }}
                />
                <span className="font-semibold text-lg">{matchDetails.home_team?.name || 'Home Team'}</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold">{realtimeScores?.home_score ?? matchDetails?.home_score ?? 0}</span>
                <span className="text-lg font-medium">VS</span>
                <span className="text-3xl font-bold">{realtimeScores?.away_score ?? matchDetails?.away_score ?? 0}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="font-semibold text-lg">{matchDetails.away_team?.name || 'Away Team'}</span>
                <img 
                  src={matchDetails.away_team?.logo || 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️'} 
                  alt={`${matchDetails.away_team?.name || 'Away Team'} logo`}
                  className="w-8 h-8 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️';
                  }}
                />
              </div>
            </div>
            
            {/* Conditional Row: Either Penalty Shootout OR Venue/Match Number */}
            {penaltyData ? (
              /* Show Penalty Shootout Scores */
              <div className="flex items-center justify-center space-x-2 mt-2 mb-2">
                <span className="text-xs text-gray-500 font-medium">penalties</span>
                <span className="text-xs font-semibold text-gray-600">({penaltyData.homeScored})</span>
                <span className="text-xs text-gray-500">-</span>
                <span className="text-xs font-semibold text-gray-600">({penaltyData.awayScored})</span>
              </div>
            ) : (
              /* Show Venue and Match Number */
              <div className="flex justify-center items-center space-x-8 text-xs">
                <span className="font-medium">{matchDetails.venue || 'TBD'}</span>
                <span className="font-medium">Match #{matchDetails.name}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Tab Content */}
        {renderTabContent()}
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onSaveAndGoBack={handleSaveAndGoBack}
        onGoBackWithoutSaving={handleGoBackWithoutSaving}
      />

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Action</h3>
            <p className="text-gray-600 mb-6">{confirmMessage}</p>
            <div className="flex space-x-3 justify-end">
              <Button
                onClick={handleCancel}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Publish All Confirmation Dialog */}
      {showPublishAllDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg 
                    className="w-6 h-6 text-green-600" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900">Publish All Events</h3>
                </div>
              </div>
              <button
                onClick={() => setShowPublishAllDialog(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to publish all key match events for this match?
              </p>
              <p className="text-sm text-gray-500">
                Proceeding will set all key match events as published for this match.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowPublishAllDialog(false)}
                disabled={isPublishingAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPublishAll}
                disabled={isPublishingAll}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPublishingAll ? 'Publishing...' : 'Publish All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Link Modal */}
      <VideoLinkModal
        isOpen={isVideoLinkModalOpen}
        onClose={() => setIsVideoLinkModalOpen(false)}
        onSuccess={async (videoUrls: string[]) => {
          console.log('Videos linked successfully:', videoUrls);
          showToastWithAutoClose('Video links updated successfully!', 'success');
          setIsVideoLinkModalOpen(false);
        }}
        matchName={matchDetails?.name}
        matchId={actualMatchId}
      />

      {/* Break Event Timing Modal */}
      {breakEventModal.eventType && (
        <BreakEventTimingModal
          isOpen={breakEventModal.isOpen}
          onClose={() => setBreakEventModal(prev => ({ ...prev, isOpen: false }))}
          onConfirm={async (timingData) => {
            // Get first video ID if not provided
            let videoId = timingData.videoId;
            if (!videoId) {
              try {
                const videosResult = await matchVideosService.getMatchVideos(actualMatchId);
                if (videosResult.success && videosResult.data && videosResult.data.length > 0) {
                  videoId = videosResult.data[0].id;
                }
              } catch (error) {
                console.warn('Could not fetch videos:', error);
              }
            }
            
            await breakEventModal.handler(
              timingData.videoTimeSeconds,
              timingData.matchMinute,
              videoId || null
            );
            setBreakEventModal(prev => ({ ...prev, isOpen: false }));
          }}
          eventType={breakEventModal.eventType}
          eventLabel={breakEventModal.eventLabel}
          defaultMatchMinute={breakEventModal.defaultMinute}
          videoPlayerRef={videoPlayerRef}
          currentVideoId={selectedVideoId}
        />
      )}

    </div>
  );
};

export default MatchDetailsScreen;
